import React, {
    useRef, useMemo, useState, useEffect, useLayoutEffect,
} from 'react';
import { setPanelPreference } from '@appblocks/frontend/redux/userSlice';
import { useDispatch, useSelector } from 'react-redux';
import {
    FaCheck, FaTrash, FaSearch, FaAngleLeft, FaAngleRight,
} from 'react-icons/fa';
import { Resizable as ReResizable } from 're-resizable';

const debounce = {};
const debounceTimeout = 500;


function getWindowDimensions() {
    const { innerWidth: width, innerHeight: height } = window;
    return {
        width,
        height,
    };
}

function useWindowDimensions() {
    const [windowDimensions, setWindowDimensions] = useState(getWindowDimensions());

    useEffect(() => {
        function handleResize() {
            setWindowDimensions(getWindowDimensions());
        }

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return windowDimensions;
}


const Resizable = ({
    children, className, resize, panelId, showToggle, show, ...props
}) => {
    const [shown, setShown] = useState(true);
    const ref = useRef(null);
    const size = useSelector(state => state.user.preferences.panels[panelId]);
    const dispatch = useDispatch();
    const { height: windowHeight, width: windowWidth } = useWindowDimensions();
    const [width, setWidth] = useState();
    const [height, setHeight] = useState(resize === 'top' || resize === 'bottom' ? 0 : '100%');
    const [isResizing, setIsResizing] = useState(false);

    const minSize = useMemo(() => {
        if (resize === 'left' || resize === 'right') {
            return 200;
        }
        return 100;
    }, [resize]);

    const dispatchNewSize = (newSize) => {
        clearTimeout(debounce[panelId]);
        const value = {
            size: newSize,
            initSize: size.initSize,
        };
        const payload = {
            name: panelId,
            value,
        };
        if (resize === 'top' || resize === 'bottom') {
            setHeight(newSize);
        } else {
            setWidth(newSize);
        }
        debounce[panelId] = setTimeout(() => {
            dispatch(setPanelPreference(payload));
            let preferences = localStorage.getItem('panels');
            if (preferences !== null) {
                preferences = JSON.parse(preferences);
                preferences = {
                    ...preferences,
                    [panelId]: value,
                };
            } else {
                preferences = {
                    [panelId]: value,
                };
            }
            localStorage.setItem('panels', JSON.stringify(preferences));
        }, debounceTimeout);
    };

    const resizeClicked = (event) => {
        setShown(!shown);
    };

    useLayoutEffect(() => {
        let newSize = size ? size.size ?? size.initSize : '100%';
        const preferences = localStorage.getItem('panels');
        if (preferences !== null) {
            const preferencesJson = JSON.parse(preferences);
            if (preferencesJson[panelId]) {
                newSize = preferencesJson[panelId].size;
                if (newSize < minSize) {
                    newSize = size.initSize;
                }
            }
        }
        dispatchNewSize(newSize);
    }, []);

    useEffect(() => {
        let preferences = localStorage.getItem('panels');
        if (preferences !== null) {
            preferences = JSON.parse(preferences);
            if (preferences[panelId]) {
                const newSize = preferences[panelId].size;
                if (newSize < minSize) {
                    dispatchNewSize(size.initSize);
                } else {
                    dispatchNewSize(newSize);
                }
                return;
            }
        }
        dispatchNewSize(size?.size ?? size?.initSize ?? 1);
    }, [shown]);

    const onDoubleClickResize = (event) => {
        event.preventDefault();
        dispatchNewSize(size.initSize);
    };

    const onResize = (e, direction, ref, d) => {
        const newSize = resize === 'top' || resize === 'bottom'
            ? parseInt(ref.style.height.replace('px', ''), 10)
            : parseInt(ref.style.width.replace('px', ''), 10);
        dispatchNewSize(newSize);
    };

    useEffect(() => {
        if (show !== undefined) {
            setShown(show);
        }
    }, [show]);

    useEffect(() => {
        if (!ref.current || resize === 'top' || resize === 'bottom') {
            return;
        }
        const mainContainerElement = ref.current.parentNode;
        resizeObserver.observe(mainContainerElement);
        return () => {
            resizeObserver.unobserve(mainContainerElement);
        };
    }, [ref.current, shown]);

    const resizeObserver = new ResizeObserver((entries) => {
        if (!ref.current || !ref.current.parentNode) {
            return;
        }
        const containerHeight = ref.current.parentNode.getBoundingClientRect().height;
        setHeight(containerHeight);
    });

    const elementStyle = useMemo(() => {
        switch (resize) {
            case 'top':
            case 'bottom':
                return {
                    paddingRight: '0',
                    width: '100%',
                    margin: '0',
                };
            case 'right':
                return {
                    paddingRight: '0',
                    maxHeight: '100%',
                };
            case 'left':
                return {
                    paddingLeft: '0',
                    // paddingRight: className === 'sidebar-container' && '0',
                    maxHeight: '100%',
                };

            default:
                break;
        }
    });

    const minWidth = useMemo(() => {
        if (shown && (resize === 'left' || resize === 'right')) {
            if (props.minWidth) {
                return props.minWidth;
            }
            return minSize;
        }
        return 1;
    }, [resize, shown]);

    const maxWidth = useMemo(() => {
        if (resize === 'left' || resize === 'right') {
            return windowWidth / 2;
        }
        return windowWidth;
    }, [resize, shown]);

    const minHeight = useMemo(() => {
        if (resize === 'top' || resize === 'bottom') {
            return minSize;
        }
        return 1;
    }, [resize, shown]);

    const adjWidth = useMemo(() => {
        switch (resize) {
            case 'left':
            case 'right':
                if (!shown) {
                    return 1;
                }
                return width;
            default:
                return '100%';
        }
    }, [width, resize, shown]);

    const resizeClass = useMemo(() => {
        switch (resize) {
            case 'top':
                return {
                    cursor: 'resize-top',
                    flex: 'position-relative',
                };
            // not used for now
            case 'bottom':
                return {
                    cursor: 'resize-bottom',
                    flex: 'flex-column-reverse',
                };
            case 'left':
                return {
                    cursor: 'resize-left',
                    flex: 'resize-toggle-container',
                };
            case 'right':
                return {
                    cursor: 'resize-right',
                    flex: 'resize-toggle-container',
                };
            default:
                return '';
        }
    }, [resize]);

    return (
        <>
            {(resize === 'left' && showToggle && !isResizing) && (
                <div
                    className="sidebar-toggle-button sidebar-toggle-right"
                    onClick={resizeClicked}
                    style={{
                        right: shown ? width : 28,
                    }}
                >
                    {shown ? (
                        <FaAngleRight />
                    ) : (
                        <FaAngleLeft />
                    )}
                </div>
            )}
            {(resize === 'right' && showToggle && !isResizing) && (
                <div
                    className="sidebar-toggle-button sidebar-toggle-left"
                    onClick={resizeClicked}
                    style={{
                        left: shown ? width : 15,
                    }}
                >
                    {shown ? (
                        <FaAngleLeft />
                    ) : (
                        <FaAngleRight />
                    )}
                </div>
            )}

            <ReResizable
                className={`${className} ${resizeClass.flex}`}
                style={{
                    ...elementStyle,
                    ...props.style,
                }}
                ref={ref}
                enable={{
                    top: resize === 'top',
                    right: resize === 'right',
                    bottom: resize === 'bottom',
                    left: resize === 'left',
                    topRight: false,
                    bottomRight: false,
                    bottomLeft: false,
                    topLeft: false,
                }}
                onResize={onResize}
                minWidth={minWidth}
                maxWidth={maxWidth}
                minHeight={minHeight}
                size={{
                    width: adjWidth,
                    height,
                }}
                handleWrapperClass={`${resizeClass.cursor}-wrapper`}
                handleClasses={{
                    top: 'resize-top',
                    right: 'resize-right',
                    bottom: 'resize-bottom',
                    left: 'resize-left',
                }}
                onResizeStart={(event) => {
                    setIsResizing(true);
                    if (event.detail === 1) {
                        return;
                    }
                    onDoubleClickResize(event);
                }}
                onResizeStop={(event) => {
                    setIsResizing(false);
                }}
            >

                {shown && (
                    <>
                        {children}
                    </>
                )}
            </ReResizable>
        </>
    );
};

export default Resizable;
